import React, { useState, useEffect, useRef, useCallback } from 'react';

interface VirtualPhotoGalleryProps {
  images: string[];
  theme: 'light' | 'dark';
  onImageFocus?: (index: number) => void;
  currentIndex?: number;
}

interface VisibleImage {
  index: number;
  src: string;
  isLoading: boolean;
  hasError: boolean;
}

const VirtualPhotoGallery: React.FC<VirtualPhotoGalleryProps> = ({
  images,
  theme,
  onImageFocus,
  currentIndex = -1
}) => {
  const [visibleImages, setVisibleImages] = useState<Map<number, VisibleImage>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Modern Intersection Observer setup with optimized options
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        setVisibleImages(prev => {
          const newMap = new Map(prev);
          
          entries.forEach(entry => {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            
            if (entry.isIntersecting) {
              // Add to visible images
              if (!newMap.has(index)) {
                newMap.set(index, {
                  index,
                  src: images[index],
                  isLoading: true,
                  hasError: false
                });
              }
            } else {
              // Remove from visible images if far from viewport
              const rect = entry.boundingClientRect;
              const viewportHeight = window.innerHeight;
              const buffer = viewportHeight * 0.5; // 50% buffer
              
              if (rect.bottom < -buffer || rect.top > viewportHeight + buffer) {
                newMap.delete(index);
              }
            }
          });
          
          return newMap;
        });
      },
      {
        root: null,
        rootMargin: '100% 0px 100% 0px', // Load 1 viewport ahead/behind
        threshold: 0
      }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, [images]);

  // Observe placeholder elements - runs after observer is created and when images change
  useEffect(() => {
    const observer = observerRef.current;
    if (!observer) return;

    // Capture refs at effect time for cleanup
    const currentRefs = itemRefs.current;

    const observeElements = () => {
      currentRefs.forEach(element => {
        if (element) observer.observe(element);
      });
    };

    // Observe elements after a microtask to ensure DOM is ready
    Promise.resolve().then(observeElements);

    return () => {
      currentRefs.forEach(element => {
        if (element) observer.unobserve(element);
      });
    };
  }, [images.length, images]);

  const handleImageLoad = useCallback((index: number) => {
    setVisibleImages(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(index);
      if (item) {
        newMap.set(index, { ...item, isLoading: false });
      }
      return newMap;
    });
  }, []);

  const handleImageError = useCallback((index: number) => {
    setVisibleImages(prev => {
      const newMap = new Map(prev);
      const item = newMap.get(index);
      if (item) {
        newMap.set(index, { ...item, isLoading: false, hasError: true });
      }
      return newMap;
    });
  }, []);

  const setItemRef = useCallback((index: number, element: HTMLDivElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  return (
    <div ref={containerRef} className="space-y-4">
      {images.map((_, index) => {
        const visibleImage = visibleImages.get(index);
        const isCurrent = currentIndex === index;

        return (
          <div
            key={index}
            id={`image-${index}`}
            className="relative"
            data-index={index}
            ref={(el) => setItemRef(index, el)}
            style={{ minHeight: '400px' }} // Maintain consistent height
          >
            {visibleImage ? (
              <div className="relative">
                {visibleImage.hasError ? (
                  <div className={`w-full h-96 max-w-[652px] ${
                    theme === 'light' ? 'bg-gray-100 text-gray-500' : 'bg-gray-800 text-gray-400'
                  } rounded flex items-center justify-center border-2 border-dashed`}>
                    <div className="text-center">
                      <div className="text-4xl mb-2">📷</div>
                      <div className="text-sm">Failed to load image {index + 1}</div>
                    </div>
                  </div>
                ) : (
                  <>
                    {visibleImage.isLoading && (
                      <div className={`absolute inset-0 w-full h-96 max-w-[652px] ${
                        theme === 'light' ? 'bg-gray-100' : 'bg-gray-800'
                      } rounded animate-pulse flex items-center justify-center z-10`}>
                        <div className={`${
                          theme === 'light' ? 'text-gray-400' : 'text-gray-500'
                        } text-sm`}>
                          Loading...
                        </div>
                      </div>
                    )}
                    <img
                      src={visibleImage.src}
                      alt={`SE Asia 35mm - ${index + 1}`}
                      className={`w-full h-auto max-w-[652px] transition-all duration-200 ${
                        isCurrent ? 'ring-2 ring-white' : 'focus:ring-2 focus:ring-white focus:outline-none'
                      } ${
                        visibleImage.isLoading ? 'opacity-0' : 'opacity-100'
                      }`}
                      loading="lazy"
                      tabIndex={0}
                      onLoad={() => handleImageLoad(index)}
                      onError={() => handleImageError(index)}
                      onFocus={() => onImageFocus?.(index)}
                    />
                  </>
                )}
              </div>
            ) : (
              // Optimized placeholder for non-visible images
              <div 
                className={`w-full h-96 max-w-[652px] ${
                  theme === 'light' ? 'bg-gray-50' : 'bg-gray-900'
                } rounded flex items-center justify-center`}
              >
                <div className={`${
                  theme === 'light' ? 'text-gray-300' : 'text-gray-600'
                } text-sm font-light`}>
                  {index + 1}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default VirtualPhotoGallery;